import { Injectable } from '@angular/core';
import type { Observable} from 'rxjs';
import { BehaviorSubject, catchError, from, map, of } from 'rxjs';
import type { AuthSession, Profile } from '../../shared/models';
import type { IUser, SystemRole } from '../../shared/models/user.model';
import {
  fetchAuthSession,
  signInWithRedirect,
  signOut,
} from 'aws-amplify/auth';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly SELECTED_ROLE_CONTEXT_KEY = 'selectedRoleContext';

  currentUser$: Observable<AuthSession | null>;
  currentUserSubject: BehaviorSubject<AuthSession | null>;
  profileSubject: BehaviorSubject<Profile | null>;
  userName: BehaviorSubject<string>  = new BehaviorSubject<string>('');

  // Store the full user data from backend
  private userDataSubject: BehaviorSubject<IUser | null> = new BehaviorSubject<IUser | null>(null);
  userData$: Observable<IUser | null> = this.userDataSubject.asObservable();

  // Selected role for the current session (shared across components)
  private selectedRoleSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  selectedRole$ = this.selectedRoleSubject.asObservable();

  // Selected role context with FULL SystemRole object (role_id, market_id, privileges, etc.)
  private selectedRoleContextSubject: BehaviorSubject<SystemRole | null> = new BehaviorSubject<SystemRole | null>(null);
  selectedRoleContext$ = this.selectedRoleContextSubject.asObservable();

  get currentUserValue(): AuthSession | null {
    return this.currentUserSubject.value;
  }

  get userDataValue(): IUser | null {
    return this.userDataSubject.value;
  }

  constructor() {
    this.currentUserSubject = new BehaviorSubject<AuthSession | null>(null);
    this.profileSubject = new BehaviorSubject<Profile | null>(null);
    this.currentUser$ = this.currentUserSubject.asObservable();

    // Load selected role context from localStorage on initialization
    this.loadSelectedRoleContextFromStorage();
  }

  loginSSO(provider: string): Observable<unknown> {
    return this.amplifyLoginSSO(provider).pipe(
      map((loginResult: unknown) => {
        return loginResult;
      }),
      catchError((e) => {
        console.error('Login error:', e);
        throw new Error(`Login failed: ${e}`);
      })
    );
  }

  logout(): void {
    this.amplifyLogout().subscribe({
      next: () => {
        this.currentUserSubject.next(null);
        // Clear selected role context from localStorage
        this.clearSelectedRoleContextFromStorage();
        this.userDataSubject.next(null);
        this.selectedRoleSubject.next(null);
        this.selectedRoleContextSubject.next(null);
        // Navigate to login page
        window.location.href = '/auth/login';
      },
      error: (error) => {
        console.error('Logout error:', error);
        // Still clear local state even if logout fails
        this.currentUserSubject.next(null);
        this.clearSelectedRoleContextFromStorage();
        this.userDataSubject.next(null);
        this.selectedRoleSubject.next(null);
        this.selectedRoleContextSubject.next(null);
        // Navigate to login page even on error
        window.location.href = '/auth/login';
      }
    });
  }

  /**
   * Set user data from backend (includes system_role)
   */
  setUserData(user: IUser | null): void {
    this.userDataSubject.next(user);
    if (user) {
      // Only set default selected role if there isn't already one (from localStorage)
      const existingRoleContext = this.getSelectedRoleContext();
      if (!existingRoleContext && user.system_role_info && user.system_role_info.length > 0) {
        // Set the first role as default
        this.setSelectedRoleContext(user.system_role_info[0]);
      } else if (existingRoleContext) {
        // Verify the stored role still exists in the user's current roles
        const roleStillExists = user.system_role_info?.some(role => {
          const roleWithRoleId = role as SystemRole & { role_id?: number };
          const existingWithRoleId = existingRoleContext as SystemRole & { role_id?: number };
          const roleId = roleWithRoleId.role_id || role.id;
          const existingId = existingWithRoleId.role_id || existingRoleContext.id;
          return roleId === existingId &&
                 role.market_id === existingRoleContext.market_id &&
                 role.business_unit_id === existingRoleContext.business_unit_id &&
                 role.assigned_at === existingRoleContext.assigned_at;
        });
        // If the stored role no longer exists, clear it and set the first available role
        if (!roleStillExists) {
          this.clearSelectedRoleContextFromStorage();
          if (user.system_role_info && user.system_role_info.length > 0) {
            this.setSelectedRoleContext(user.system_role_info[0]);
          }
        }
      }
    }
  }

  setSelectedRole(role: string | null): void {
    this.selectedRoleSubject.next(role);
  }

  /**
   * Set the selected role context with FULL SystemRole object
   * This includes role_id, market_id, market_name, business_unit_id, privileges, etc.
   * Use this for market-based role switching
   */
  setSelectedRoleContext(roleContext: SystemRole | null): void {
    this.selectedRoleContextSubject.next(roleContext);
    if (roleContext) {
      // Save to localStorage
      this.saveSelectedRoleContextToStorage(roleContext);
    } else {
      this.clearSelectedRoleContextFromStorage();
    }
  }

  /**
   * Get the current selected role context from memory
   */
  getSelectedRoleContext(): SystemRole | null {
    return this.selectedRoleContextSubject.value;
  }

  /**
   * Save selected role context to localStorage
   */
  private saveSelectedRoleContextToStorage(roleContext: SystemRole): void {
    try {
      localStorage.setItem(this.SELECTED_ROLE_CONTEXT_KEY, JSON.stringify(roleContext));
    } catch (error) {
      console.error('Error saving selected role context to localStorage:', error);
    }
  }

  /**
   * Load selected role context from localStorage
   */
  private loadSelectedRoleContextFromStorage(): void {
    try {
      const storedRoleContext = localStorage.getItem(this.SELECTED_ROLE_CONTEXT_KEY);
      if (storedRoleContext) {
        const roleContext: SystemRole = JSON.parse(storedRoleContext);
        this.selectedRoleContextSubject.next(roleContext);
        this.selectedRoleSubject.next(roleContext.role_name);
      }
    } catch (error) {
      console.error('Error loading selected role context from localStorage:', error);
      this.clearSelectedRoleContextFromStorage();
    }
  }

  /**
   * Clear selected role context from localStorage
   */
  private clearSelectedRoleContextFromStorage(): void {
    try {
      localStorage.removeItem(this.SELECTED_ROLE_CONTEXT_KEY);
    } catch (error) {
      console.error('Error clearing selected role context from localStorage:', error);
    }
  }



  getUserBySession(): Observable<AuthSession | undefined> {
    return this.amplifyGetUserSession().pipe(
      map((user: unknown) => {
        if (user) {
          const result = this.getUserAuthDataMap(user);
          this.currentUserSubject.next(result);
          return result;
        }
        this.logout();
        return undefined;
      }),
      catchError(() => {
        return of(undefined);
      })
    );
  }



  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  getUserAuthDataMap(loginResult: any): AuthSession {
    const result: AuthSession = {
      userId: loginResult.userSub,
      session: loginResult.tokens,
      payload: {
        prid: loginResult.tokens.idToken.payload?.identities?.[0]?.userId?.replace('@astrazeneca.net', '') ?? '',
        sub: loginResult.tokens.idToken.payload.sub,
        firstname: loginResult.tokens.idToken.payload.given_name,
        lastname: loginResult.tokens.idToken.payload.middle_name,
        email: loginResult.tokens.idToken.payload.email,
        email_verified: loginResult.tokens.idToken.payload.email_verified,
        groups: loginResult.tokens.idToken.payload['cognito:groups']
          ? loginResult.tokens.idToken.payload['cognito:groups']
          : '',
        adgroups: loginResult.tokens.idToken.payload['custom:adgroups']
          ? loginResult.tokens.idToken.payload['custom:adgroups']
          : '',
        exp: loginResult.tokens.idToken.payload.exp
          ? loginResult.tokens.idToken.payload.exp
          : 0,
      },
    };
    this.userName.next(`${result.payload.firstname} ${result.payload.lastname}`);
    return result;
  }

  getProfile(): Profile {
    if (!this.currentUserValue?.session?.idToken?.payload) {
      return {} as Profile;
    }

    const payload = this.currentUserValue.session.idToken.payload;
    const { email, middle_name, given_name } = payload;
    const groups = payload['custom:adgroups'];

    // Handle groups processing with null checks
    let processedGroups: string[] = [];
    if (typeof groups === 'string') {
      processedGroups = groups[0] === '['
        ? groups.substring(1, groups.length - 1).split(', ')
        : groups.split(' ');
    }

    let prid = '';
    const identities = payload?.['identities'];
    if (Array.isArray(identities) && identities.length > 0 && typeof identities[0] === 'object' && identities[0] !== null && 'userId' in identities[0]) {
      prid = (identities[0] as { userId?: string }).userId ?? '';
    }
    const profile: Profile = {
      prid: typeof prid === 'string' ? prid?.replace('@astrazeneca.net', '') : '',
      email: typeof email === 'string' ? email : '',
      userPrincipalName: typeof given_name === 'string' ? given_name : '',
      familyName: typeof middle_name === 'string' ? middle_name : '',
      givenName: typeof given_name === 'string' ? given_name : '',
      groups: processedGroups,
      lastUpdate: new Date().toDateString(),
    };

    this.profileSubject.next(profile);

    return profile;
  }

  // http auth service
  amplifyLoginSSO(provider: string): Observable<unknown> {
    try {
      const authResult = signInWithRedirect({
        provider: {
          custom: provider,
        },
      });
      return from(authResult);
    } catch (error) {
      throw new Error('AZ-AUTH-LOGIN-ERROR: ' + error);
    }
  }

  /**
   * Logs the user out and redirects to the logout page
   */
  amplifyLogout(): Observable<unknown> {
    try {
      // Clear local tokens only (don't navigate to Cognito logout endpoint)
      // The Cognito logout URL configuration issue causes redirect_uri error
      const logoutResult = signOut({ global: false });
      return from(logoutResult);
    } catch (error) {
      throw new Error('AZ-AUTH-LOGOUT-ERROR: ' + error);
    }
  }

  amplifyGetUserSession(): Observable<unknown> {
    try {
      const authResult = fetchAuthSession();
      return from(authResult);
    } catch (error) {
      throw new Error('AZ-AUTH-GETUSER-ERROR: ' + error);
    }
  }

  /**
   * Get the current token expiration time in seconds (Unix timestamp)
   * Returns null if no valid session
   */
  getTokenExpirationTime(): number | null {
    const session = this.currentUserValue;
    if (!session?.session?.idToken?.payload?.exp) {
      return null;
    }
    return session.session.idToken.payload.exp;
  }

  /**
   * Check if the current token has more than the specified minutes remaining
   * @param minutes - Number of minutes to check
   * @returns true if token has more than specified minutes remaining, false otherwise
   */
  hasTokenTimeRemaining(minutes: number): boolean {
    const expirationTime = this.getTokenExpirationTime();
    if (!expirationTime) {
      return false;
    }

    const nowInSeconds = Math.floor(Date.now() / 1000);
    const timeRemainingInSeconds = expirationTime - nowInSeconds;
    const timeRemainingInMinutes = timeRemainingInSeconds / 60;

    return timeRemainingInMinutes > minutes;
  }

  /**
   * Get time remaining on current token in minutes
   * Returns null if no valid session
   */
  getTokenTimeRemainingInMinutes(): number | null {
    const expirationTime = this.getTokenExpirationTime();
    if (!expirationTime) {
      return null;
    }

    const nowInSeconds = Math.floor(Date.now() / 1000);
    const timeRemainingInSeconds = expirationTime - nowInSeconds;
    return timeRemainingInSeconds / 60;
  }

  /**
   * Refresh the authentication token
   * Forces Cognito to refresh the session token
   */
  refreshAuthToken(): Observable<AuthSession> {
    try {
      // Fetch a new session with forceRefresh option
      const authResult = fetchAuthSession({ forceRefresh: true });
      return from(authResult).pipe(
        map((session: unknown) => {
          const result = this.getUserAuthDataMap(session);
          this.currentUserSubject.next(result);
          return result;
        }),
        catchError((error) => {
          console.error('Token refresh failed:', error);
          throw new Error('AZ-AUTH-REFRESH-ERROR: ' + error);
        })
      );
    } catch (error) {
      throw new Error('AZ-AUTH-REFRESH-ERROR: ' + error);
    }
  }
}
